import React, { useEffect } from "react";
import { Container } from "@material-ui/core";
import HeroSection from "../HeroSection";
import ServiceHighlights from "./ServiceHighlights";
import PopularRides from "../PopularRides";
import FemaleOnly from "../FemaleOnly";
import Timeline from "../../pages/HowItWork"; // Importing Timeline as HowItWorks
import HowItWorksForDriver from "../../pages/HowItWorkforDriver";
import SilkCabsFooter from "../SilkCabsFooter";
import HelpSection from "../HelpSection";
import ReactGA from "react-ga4";
import styled from "styled-components";
import mobile from "../../assets/images/mobile.png";
import { Helmet } from "react-helmet";

const Home = () => {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/", title: "Home Page" });
  }, []);

  return (
    <Mainwrapper>
      <Helmet>
        <title>GoSawari - Ride in Style | Inclusive Carpooling Pakistan</title>
        <meta
          name="description"
          content="Experience premium, inclusive carpooling in Pakistan for both men and women. Book city rides, outstation trips, and rentals with GoSawari safely."
        />
      </Helmet>

      {/* Hero Section */}
      <HeroSection />

      {/* App Download / Coming Soon Section */}
      <ComingSoonSection />

      {/* Services Section */}
      <ServiceHighlights />

      {/* Popular Rides Section */}
      <PopularRides />

      {/* Female Only Section */}
      <FemaleOnly />

      {/* How It Works (Passenger) */}
      <Timeline />

      {/* How It Works (Driver) */}
      <HowItWorksForDriver />

      {/* Help Section */}
      <HelpSection />

      {/* Footer */}
      <FooterWrapper>
        <SilkCabsFooter />
      </FooterWrapper>
    </Mainwrapper>
  );
};
export default Home;

const ComingSoonSection = () => {
  return (
    <AppSectionContainer>
      <AppContent>
        <AppTextContent>
          <AppTitle>Get the GoSawari App</AppTitle>
          <AppSubtitle>
            Unlock exclusive features, real-time tracking, and seamless booking
            on your mobile today.
          </AppSubtitle>
          <StoreButtons>
            <StoreBtn as="a" href="#">
              <span>Download on the</span>
              <strong>App Store</strong>
            </StoreBtn>
            <StoreBtn
              as="a"
              href="https://play.google.com/store/apps/details?id=com.gosawari.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Get it on</span>
              <strong>Google Play</strong>
            </StoreBtn>
          </StoreButtons>
        </AppTextContent>
        <AppImageWrapper>
          <AppPhoneImage
            src={mobile || "https://via.placeholder.com/300"}
            alt="GoSawari App"
          />
          <GlowCircle />
        </AppImageWrapper>
      </AppContent>
    </AppSectionContainer>
  );
};

// Styled Components

const Mainwrapper = styled.div`
  background-color: white; /* Clean Light Background */
  min-height: 100vh;
  color: #37474f; /* Dark Text */
  overflow-x: hidden;
`;

const FooterWrapper = styled.div`
  background: #f4f6f8;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;
`;

const AppSectionContainer = styled.section`
  padding: 6rem 2rem;
  background: #054752; /* Primary Dark Teal for Contrast */
  background: linear-gradient(135deg, #054752 0%, #004d40 100%);
  display: flex;
  justify-content: center;
  position: relative;
  overflow: hidden;
  color: white;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const AppContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1100px;
  width: 100%;
  gap: 4rem;

  @media (max-width: 1024px) {
    gap: 2rem;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AppTextContent = styled.div`
  flex: 1;
`;

const AppTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;
  letter-spacing: -1px;

  @media (max-width: 1024px) {
    font-size: 2.2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    line-height: 1.25;
  }
`;

const AppSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e0f2f1;
  line-height: 1.6;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const StoreButtons = styled.div`
  display: flex;
  gap: 1.5rem;

  @media (max-width: 900px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StoreBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 0.8rem 1.5rem;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 160px;

  @media (max-width: 480px) {
    width: 100%;
    max-width: 200px;
  }

  span {
    font-size: 0.7rem;
    text-transform: uppercase;
    opacity: 0.8;
  }

  strong {
    font-size: 1.1rem;
    font-weight: 600;
  }

  &:hover {
    background: white;
    color: #054752;
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const AppImageWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;

  @media (max-width: 900px) {
    margin-top: 3rem;
  }
`;

const AppPhoneImage = styled.img`
  width: 300px;
  z-index: 2;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.4));
  transform: rotate(-5deg);
  transition: transform 0.5s ease;

  @media (max-width: 768px) {
    width: 240px;
  }

  &:hover {
    transform: rotate(0deg) scale(1.05);
  }
`;

const GlowCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 350px;
  height: 350px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.15) 0%,
    transparent 70%
  );
  z-index: 1;
  border-radius: 50%;
`;
